import { call } from '@advanza/api'
import { changeEntity } from 'actions/entities'
import {
    addPresenceSubscriptionCharges,
    addPresenceSubscriptionDiscount,
    deletePresenceSubscriptionDiscount,
    fetchPresenceSubscription,
    fetchProvider,
    updatePresenceSubscription,
} from 'actions/providers'
import {
    changeSubscriptionFilter,
    fetchAllMunicipalities,
    fetchSubscription,
    findGeoIdsByRange,
    replaceSubscriptionMunicipalities,
    saveSubscriptionGeoids,
    toggleSubscriptionMunicipalityIds,
    toggleSubscriptionMunicipalityIdsMany,
} from 'actions/subscriptions'
import { useAppDispatch, useAppSelector } from 'hooks/hooks'
import LeadSettings from 'interfaces/LeadSettings/LeadSettings'
import {
    ProviderPresenceSubscriptionData,
    ProvidersPresenceDiscount,
    ProvidersPresenceSubscription,
    TYPE_AMOUNT,
    TYPE_PERCENTAGE,
} from 'interfaces/PresenceSubscription'
import { Provider } from 'interfaces/Providers'
import { ReplacementLeadStats } from 'interfaces/ReplacementLeads'
import Service from 'interfaces/Services'
import { Subscription } from 'interfaces/Subscriptions'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

interface RootState {
    providers: {
        entities: {
            providers: Record<number, Provider> | undefined
            users: Record<number, any>
            leadSettings: Record<number, LeadSettings>
        }
        presenceSubscription: {
            isFetching: boolean
            entities: Record<
                number,
                {
                    subscription: ProvidersPresenceSubscription | null
                    allowPresenceSubscription: boolean
                    serviceId?: number
                    amount?: number
                    overrideAmount?: number
                    error?: string
                }
            >
        }
    }
    services: {
        entities: {
            services: Record<number, Service>
        }
    }
    subscriptions: {
        entities: {
            subscriptions: Record<number, Subscription>
            services: Record<number, Service>
        }
        result: Record<string, Record<number, number[]>>
        filters: Record<string, any>
        municipalities: {
            result: any
            entities: any
            mapCenter: any
        }
        isFetching: boolean
    }
}

export type SubscriptionStatus = 'active' | 'inactive'
export type DiscountStatus = 'active' | 'inactive'

export const SUBSCRIPTION_STATUS = {
    ACTIVE: 'active' as SubscriptionStatus,
    INACTIVE: 'inactive' as SubscriptionStatus,
} as const

export const DISCOUNT_STATUS = {
    ACTIVE: 'active' as DiscountStatus,
    INACTIVE: 'inactive' as DiscountStatus,
} as const

export function useReplacementLeadStats(providerId: number) {
    const [stats, setStats] = useState<ReplacementLeadStats | null>(null)
    const [isFetching, setIsFetching] = useState(true)

    useEffect(() => {
        if (!providerId) {
            setStats(null)
            setIsFetching(false)
            return
        }

        let isCurrent = true
        setStats(null)
        setIsFetching(true)
        call(`office/providers/get-replacement-lead-stats/${providerId}`)
            .then((response) => {
                if (isCurrent) {
                    setStats(response as ReplacementLeadStats)
                }
            })
            .finally(() => {
                if (isCurrent) {
                    setIsFetching(false)
                }
            })

        return () => {
            isCurrent = false
        }
    }, [providerId])

    return { stats, isFetching }
}

export function useProvider(id: number) {
    const { providers, ...entities } = useSelector((state: RootState) => state.providers.entities)
    const { services } = useSelector((state: RootState) => state.services.entities)
    const provider = providers && providers[id]
    const user = provider?.user ? entities.users[provider.user] : undefined
    const leadSettings = provider?.lead_setting
        ? entities.leadSettings[provider.lead_setting]
        : undefined

    const isFranchiseMain = provider?.franchise_status ? provider.franchise_status > 0 : false
    const useFranchiseBilling = provider?.payment_info_franchise || false
    const dispatch = useAppDispatch()
    let websiteWithProtocol = provider?.website ? provider.website.toLowerCase() : ''

    if (!websiteWithProtocol.includes('http')) {
        websiteWithProtocol = 'http://' + websiteWithProtocol
    }

    useEffect(() => {
        if (id) {
            dispatch(fetchProvider(id, false))
        }
    }, [dispatch, id])

    const updatePrimarySubscription = async (subscriptionId: number) => {
        try {
            const response = await call(`office/providers/update-primary-subscription/${id}`, {
                method: 'post',
                body: JSON.stringify({ primary_subscription_id: subscriptionId }),
            })

            // Refresh the provider data to get the updated primary_subscription_id
            dispatch(fetchProvider(id, true))

            return response
        } catch (error) {
            console.error('Failed to update primary subscription:', error)
            throw error
        }
    }

    return {
        provider,
        leadSettings,
        services,
        entities,
        user,
        isFranchiseMain,
        useFranchiseBilling,
        websiteWithProtocol,
        updatePrimarySubscription,
    }
}

export function useProviderStatsSettings(id: number) {
    const [isSaving, setIsSaving] = useState(false)
    const dispatch = useAppDispatch()

    const onRemoveSlowLabel = () => {
        if (isSaving) {
            return false
        }
        setIsSaving(true)
        return call('office/providers/remove-slow-label/' + id, { method: 'post' })
            .then(() => {
                return call('office/providers/reset-stats-date/' + id, {
                    method: 'post',
                }).then(() => {
                    dispatch(fetchProvider(id, true))
                })
            })
            .finally(() => setIsSaving(false))
    }

    const onResetFreeTrialDayLimit = () => {
        if (isSaving) {
            return false
        }
        setIsSaving(true)
        return call('office/providers/reset-free-trial-day-limit/' + id, { method: 'post' })
            .then(() => {
                dispatch(fetchProvider(id, true))
            })
            .finally(() => setIsSaving(false))
    }

    return {
        onRemoveSlowLabel,
        onResetFreeTrialDayLimit,
        isSaving,
    }
}

/**
 * Toggle the franchise-wide review pool.
 *
 * The setting belongs to the franchise, so the backend writes it to the franchise main whatever
 * account it is operated from, and the provider is refetched afterwards to pick the resolved
 * value back up.
 */
export function useCentralizedReviewCollection(providerId: number) {
    const [isSaving, setIsSaving] = useState(false)
    const dispatch = useAppDispatch()

    const setEnabled = async (enabled: boolean) => {
        setIsSaving(true)

        try {
            return await call(
                `office/providers/update-centralized-review-collection/${providerId}`,
                { method: 'post', json: { enabled } },
            )
        } finally {
            dispatch(fetchProvider(providerId, true))
            setIsSaving(false)
        }
    }

    return { setEnabled, isSaving }
}

export function useGetSubscriptions(providerId: number, fetch = false) {
    const dispatch = useAppDispatch()
    const filterId = 'p_' + providerId

    useEffect(() => {
        if (fetch) {
            dispatch(
                changeSubscriptionFilter(filterId, {
                    defaultOptions: { providerIds: [providerId] },
                }),
            )
        }
    }, [dispatch, fetch, filterId, providerId])

    const { entities, result, filters } = useSelector((state: RootState) => state.subscriptions)
    const filter = filters[filterId] || {}
    const pages = result[filter.searchKey] || {}
    const results = pages[0] || []
    const subscriptionArr = results
        .map((id: number) => (entities.subscriptions || {})[id])
        .filter((subscription: Subscription) => subscription)

    return {
        subscriptionArr,
        ...entities,
    }
}

interface ResetCooldown {
    days_remaining: number
    next_allowed: string
}

export function useQualityScores(providerId: number) {
    const [isLoading, setIsLoading] = useState(false)
    const [debugInfo, setDebugInfo] = useState<Record<number, unknown>>({})
    const [didInvalidate, setDidInvalidate] = useState(false)
    const [isReCalcing, setIsReCalcing] = useState(false)
    const [resetCooldown, setResetCooldown] = useState<ResetCooldown | null>(null)
    const dispatch = useAppDispatch()
    const provider = useSelector(
        (state: RootState) =>
            state.providers.entities.providers && state.providers.entities.providers[providerId],
    )

    useEffect(() => {
        if ((provider && !provider.quality_scores) || didInvalidate) {
            setIsLoading(true)
            call('office/providers/get-quality-scores/' + providerId)
                .then((response) => {
                    dispatch(
                        changeEntity({
                            store: 'providers',
                            name: 'providers',
                            key: providerId,
                            filterId: 'all',
                            remove: false,
                            newKey: null,
                            dontTouch: false,
                            preservePreviousTouch: false,
                            diff: { quality_scores: response.items },
                        }),
                    )
                    setResetCooldown(response.resetCooldown ?? null)
                })
                .finally(() => {
                    setDidInvalidate(false)
                    setIsLoading(false)
                })
        }
    }, [didInvalidate, provider, providerId, dispatch])

    const invalidate = () => setDidInvalidate(true)
    const recalcQualityScore = (providerId: number) => {
        if (isReCalcing) {
            return Promise.resolve()
        }
        setIsReCalcing(true)
        return call(`office/providers/recalc-quality_scores/${providerId}`)
            .then((response) => {
                setIsReCalcing(false)
                setDebugInfo(response.out)
            })
            .finally(invalidate)
    }

    const resetQualityScore = (
        startDate: string,
    ): Promise<{ success: boolean; error?: string }> => {
        return call(`office/providers/reset-quality-score/${providerId}`, {
            method: 'post',
            payload: { start_date: startDate },
        }).then(
            () => {
                setResetCooldown(null)
                dispatch(fetchProvider(providerId, true))
                invalidate()
                return { success: true }
            },
            (rejection) => {
                if (rejection?.error === 'quality_score_reset_cooldown') {
                    setResetCooldown({
                        days_remaining: rejection.days_remaining,
                        next_allowed: rejection.next_allowed,
                    })
                }
                const error = rejection?.error ?? rejection?.msg ?? 'Something went wrong'
                return { success: false, error }
            },
        )
    }

    return {
        scores: provider?.quality_scores || [],
        recalcQualityScore,
        resetQualityScore,
        resetCooldown,
        isLoading,
        debugInfo,
        statsStartDate: provider?.stats_start_date,
        isReCalcing,
    }
}

export function useSubscriptionArea(subscriptionId: string, isLeadEstimator: boolean) {
    const dispatch = useAppDispatch()
    const [isSaving, setIsSaving] = useState(false)
    const { entities, isFetching } = useAppSelector((state) => state.subscriptions)
    const {
        result: municipalitiesGrouped,
        entities: municipalities,
        mapCenter,
    } = useAppSelector((state) => state.subscriptions.municipalities)

    useEffect(() => {
        if (!isLeadEstimator) {
            dispatch(fetchSubscription(subscriptionId)).then(() => {
                dispatch(fetchAllMunicipalities(subscription.country_code))
            })
        }
    }, [])
    const subscription = entities.subscriptions && entities.subscriptions[subscriptionId]
    const service = subscription && entities.services && entities.services[subscription.service_id]
    const postalCode = subscription && subscription.postal_codes && subscription.postal_codes[0]
    const subscriptionMunicipalities = subscription && subscription.subscriptions_municipalities
    const replaceMunicipalities = (newIds: number[]) => {
        dispatch(replaceSubscriptionMunicipalities(subscriptionId, newIds))
    }

    function toggleMunicipality(id: number) {
        dispatch(toggleSubscriptionMunicipalityIds(subscriptionId, id))
    }

    function setCenter(coords: { latitude: number; longitude: number }) {
        dispatch({ type: 'SERVICE_AREA_SET_MAP_CENTER', ...coords })
    }

    function toggleMunicipalityMany(ids: number[], hasAll: boolean) {
        dispatch(toggleSubscriptionMunicipalityIdsMany(subscriptionId, ids, hasAll))
    }

    function setByRadius() {
        findGeoIdsByRange(
            postalCode.latitude,
            postalCode.longitude,
            postalCode.distance,
            subscription.country_code,
        ).then((municipalityIds) => {
            setCenter(postalCode)
            replaceMunicipalities(municipalityIds)
            saveMunicipalities()
        })
    }

    const saveMunicipalities = () => {
        if (isLeadEstimator) {
            return Promise.resolve()
        }
        if (!isSaving) {
            setIsSaving(true)
            dispatch(saveSubscriptionGeoids(subscriptionId)).finally(() => setIsSaving(false))
        }
    }

    return {
        municipalitiesGrouped,
        toggleMunicipality,
        setCenter,
        replaceMunicipalities,
        subscriptionMunicipalities,
        saveMunicipalities,
        mapCenter,
        municipalities,
        isFetching,
        isSaving,
        setByRadius,
        toggleMunicipalityMany,
        postalCode,
        subscription,
        service,
    }
}

export function usePresenceSubscription(providerId: number) {
    const dispatch = useAppDispatch()
    const { presenceSubscription } = useSelector((state: RootState) => state.providers)
    const providerData: ProviderPresenceSubscriptionData =
        presenceSubscription.entities[providerId] || {}
    const {
        subscription,
        allowPresenceSubscription,
        serviceId,
        amount,
        overrideAmount,
        error,
        isUpdating,
    } = providerData

    useEffect(() => {
        if (providerId) {
            dispatch(fetchPresenceSubscription(providerId))
        }
    }, [dispatch, providerId])

    const totalDiscount = (() => {
        if (!subscription?.presence_discounts) return 0

        // Calculate the next billing month (what will be charged at the end of current month)
        // Since we charge at the end of the month for the upcoming month, we need to check
        // which discounts will be active for the next billing cycle
        const now = new Date()
        const nextBillingMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1) // First day of next month

        const activeDiscounts = subscription.presence_discounts.filter(
            (discount: ProvidersPresenceDiscount) => {
                // Must be active status
                if (discount.status !== DISCOUNT_STATUS.ACTIVE) return false

                // Check if discount will be active for the next billing month
                const startDate = discount.start_date ? new Date(discount.start_date) : null
                const endDate = discount.end_date ? new Date(discount.end_date) : null

                // If no start date, consider it active (backward compatibility)
                if (!startDate) return true

                // If start date is after the next billing month, not active yet
                if (startDate > nextBillingMonth) return false

                // If end date exists and is before the next billing month, not active anymore
                if (endDate && endDate < nextBillingMonth) return false

                return true
            },
        )

        if (activeDiscounts.length === 0) return 0

        const baseAmount = overrideAmount ?? amount ?? 0

        // Find the highest percentage discount
        const percentageDiscounts = activeDiscounts.filter(
            (discount: ProvidersPresenceDiscount) => discount.type === TYPE_PERCENTAGE,
        )
        const highestPercentageDiscount =
            percentageDiscounts.length > 0
                ? Math.max(
                      ...percentageDiscounts.map((discount: ProvidersPresenceDiscount) =>
                          Number(discount.amount),
                      ),
                  )
                : 0

        // Sum all amount-based discounts
        const amountDiscounts = activeDiscounts
            .filter((discount: ProvidersPresenceDiscount) => discount.type === TYPE_AMOUNT)
            .reduce((sum, discount: ProvidersPresenceDiscount) => sum + Number(discount.amount), 0)

        // Calculate total: highest percentage of base amount + all amount discounts
        return baseAmount * (highestPercentageDiscount / 100) + amountDiscounts
    })()

    const totalFee = (() => {
        if (overrideAmount) return Math.max(0, overrideAmount - totalDiscount)
        if (amount) return Math.max(0, amount - totalDiscount)
        return 0
    })()

    const updateSubscription = async (subscriptionData: Partial<ProvidersPresenceSubscription>) => {
        return dispatch(updatePresenceSubscription(providerId, subscriptionData))
    }

    const addSubscriptionDiscount = async (discountData: ProvidersPresenceDiscount) => {
        return dispatch(addPresenceSubscriptionDiscount(providerId, discountData))
    }

    const deleteSubscriptionDiscount = async (discountId: number) => {
        return dispatch(deletePresenceSubscriptionDiscount(providerId, discountId))
    }

    const addSubscriptionCharges = async () => {
        return dispatch(addPresenceSubscriptionCharges(providerId))
    }

    return {
        subscription,
        isLoading: presenceSubscription.isFetching,
        isUpdating: isUpdating || false,
        allowPresenceSubscription,
        error,
        serviceId,
        amount,
        overrideAmount,
        totalDiscount,
        totalFee,
        updateSubscription,
        addSubscriptionDiscount,
        deleteSubscriptionDiscount,
        addSubscriptionCharges,
    }
}
